<template>
  <v-container fluid class="disposition-report-container pa-0 ma-0">
    <div class="disposition-report-shell">
      <v-progress-linear
        v-if="loading"
        class="loading-bar"
        color="primary"
        height="3"
        indeterminate
      />

      <!-- Header -->
      <section v-if="showHeader" class="header-section disposition-report-hero mb-4">
        <v-btn icon small class="header-close-btn" @click="dismissHeader">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
        <div class="d-flex align-center mb-2">
          <div class="header-icon-wrapper mr-4">
            <v-icon size="32" color="white">mdi-chart-box-outline</v-icon>
          </div>
          <div class="flex-grow-1">
            <h1 class="page-title">Disposition Report</h1>
            <p class="page-subtitle mb-0">
              Agent call volume and disposition analytics by team, date range, and member.
            </p>
          </div>
          <div class="d-none d-sm-flex">
            <v-chip
              class="mr-2 feature-chip"
              small
              outlined
            >
              <v-icon left x-small color="white">mdi-account-group</v-icon>
              <span class="white--text">Team Filter</span>
            </v-chip>
            <v-chip
              v-if="!$store.state.user.permissions.disableExportList"
              class="feature-chip"
              small
              outlined
            >
              <v-icon left x-small color="white">mdi-file-export</v-icon>
              <span class="white--text">Export</span>
            </v-chip>
          </div>
        </div>
        <div class="feature-pills-container">
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-tag-multiple</v-icon>
            Disposition Breakdown
          </div>
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-account-tie</v-icon>
            Agent Performance
          </div>
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-calendar-range</v-icon>
            Date Range
          </div>
          <div class="feature-pill">
            <v-icon small class="mr-2" color="white">mdi-file-export</v-icon>
            Excel Export
          </div>
        </div>
      </section>

      <!-- Filter Card -->
      <div class="modern-card customBorderRadius mb-4">
        <div class="card-header-green"></div>
        <div class="pa-4 pb-3">
          <div class="d-flex align-center mb-4">
            <v-icon left color="primary">mdi-filter-variant</v-icon>
            <span class="text-subtitle-1 font-weight-medium">Filters</span>
          </div>

          <div class="filter-grid">
            <!-- Members -->
            <div class="filter-field-wrap">
              <div class="filter-field-label">
                <v-icon x-small class="mr-1" color="grey darken-1">mdi-account-multiple</v-icon>
                Members
              </div>
              <v-autocomplete
                v-model="selectedUser"
                :items="allUsers"
                item-text="name"
                item-value="id"
                label="Select member"
                outlined
                dense
                hide-details
                multiple
                class="filter-input"
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0" class="filter-selection-text">{{ item.name }}</span>
                  <span v-if="index === 1" class="grey--text caption ml-1">
                    (+{{ selectedUser.length - 1 }} others)
                  </span>
                </template>
              </v-autocomplete>
            </div>

            <!-- Date Range -->
            <div class="filter-field-wrap">
              <div class="filter-field-label">
                <v-icon x-small class="mr-1" color="grey darken-1">mdi-calendar-range</v-icon>
                Date Range
              </div>
              <v-dialog
                ref="dialog"
                v-model="modal"
                :return-value.sync="dates"
                persistent
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateRangeLabel"
                    label="Pick a date range"
                    prepend-inner-icon="mdi-calendar"
                    outlined
                    dense
                    hide-details
                    readonly
                    class="filter-input"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="dates" color="primary" scrollable range>
                  <v-spacer />
                  <v-btn text color="primary" @click="modal = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.dialog.save(dates)">
                    OK
                  </v-btn>
                </v-date-picker>
              </v-dialog>
            </div>

            <!-- Exclude Dispositions -->
            <div class="filter-field-wrap">
              <div class="filter-field-label">
                <v-icon x-small class="mr-1" color="grey darken-1">mdi-tag-off</v-icon>
                Exclude Dispositions
              </div>
              <v-autocomplete
                v-model="excludedDispositions"
                :items="dispositionOptions"
                item-text="title"
                item-value="title"
                label="None selected"
                outlined
                dense
                hide-details
                multiple
                clearable
                class="filter-input"
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0" class="filter-selection-text">{{ item.title }}</span>
                  <span v-if="index === 1" class="grey--text caption ml-1">
                    (+{{ excludedDispositions.length - 1 }} others)
                  </span>
                </template>
              </v-autocomplete>
            </div>

            <!-- Actions -->
            <div class="filter-field-wrap filter-actions-wrap">
              <div class="filter-field-label">&nbsp;</div>
              <div class="filter-actions">
                <v-btn color="primary" depressed class="search-btn" @click="loadReport">
                  <v-icon left small>mdi-magnify</v-icon>
                  Search
                </v-btn>
                <v-btn
                  v-if="!$store.state.user.permissions.disableExportList"
                  color="primary"
                  outlined
                  class="export-btn"
                  :disabled="!hasData"
                  @click="exportReport"
                >
                  <v-icon left small>mdi-download</v-icon>
                  Export
                </v-btn>
              </div>
            </div>
          </div>

          <v-divider class="mt-3 mb-2" />
          <p class="filter-summary mb-0">
            <v-icon x-small class="mr-1" color="grey">mdi-information-outline</v-icon>
            Team: <strong>{{ selectedTeamLabel }}</strong> &nbsp;&middot;&nbsp; {{ dateRangeLabel || "Select dates" }}
          </p>
        </div>
      </div>

      <!-- KPI Cards -->
      <div v-if="hasData" class="kpi-grid mb-4" aria-label="Report summary">
        <div class="kpi-card modern-card customBorderRadius">
          <div class="card-header-green"></div>
          <div class="pa-4">
            <p class="kpi-label">Total Calls</p>
            <p class="kpi-value">{{ formatNumber(summary.totalCalls) }}</p>
          </div>
        </div>
        <div class="kpi-card modern-card customBorderRadius">
          <div class="card-header-green"></div>
          <div class="pa-4">
            <p class="kpi-label">Connection Rate</p>
            <p class="kpi-value">{{ summary.connectionRate }}%</p>
            <p class="kpi-sub">{{ formatNumber(summary.connectedCalls) }} connected</p>
          </div>
        </div>
        <div class="kpi-card modern-card customBorderRadius">
          <div class="card-header-green"></div>
          <div class="pa-4">
            <p class="kpi-label">Avg Time Spent</p>
            <p class="kpi-value">{{ formatMinutes(summary.avgTimeSpent) }}</p>
            <p class="kpi-sub">per call</p>
          </div>
        </div>
        <div class="kpi-card modern-card customBorderRadius">
          <div class="card-header-green"></div>
          <div class="pa-4">
            <p class="kpi-label">Agents</p>
            <p class="kpi-value">{{ formatNumber(agents.length) }}</p>
            <p class="kpi-sub">Avg {{ formatNumber(benchmarks.avgCalls) }} calls each</p>
          </div>
        </div>
        <div class="kpi-card modern-card customBorderRadius">
          <div class="card-header-green"></div>
          <div class="pa-4">
            <div class="d-flex justify-space-between align-center mb-1">
              <p class="kpi-label mb-0">Successful / Unsuccessful</p>
              <v-btn icon small title="Outcome settings" @click="openOutcomeSettings" class="ma-n2">
                <v-icon color="primary" small>mdi-cog-outline</v-icon>
              </v-btn>
            </div>
            <p class="kpi-value" style="font-size: 22px;">
              <span class="success--text">{{ formatNumber(outcomes.successfulCalls) }}</span>
              <span style="color: #ccc; font-weight: 400; margin: 0 4px;">/</span>
              <span class="error--text">{{ formatNumber(outcomes.unsuccessfulCalls) }}</span>
            </p>
            <p class="kpi-sub">
              {{ outcomes.successfulRate }}% <span style="color: #ccc;">/</span> {{ outcomes.unsuccessfulRate }}% of total
            </p>
          </div>
        </div>
      </div>

      <!-- Loading / Empty -->
      <div v-if="loading && !hasData" class="modern-card customBorderRadius loading-card">
        <div class="card-header-green"></div>
        <div class="pa-6 text-center">
          <v-progress-circular indeterminate color="primary" size="32" class="mb-3" />
          <p class="mb-0">Loading disposition report...</p>
        </div>
      </div>

      <div v-else-if="!loading && !hasData" class="modern-card customBorderRadius loading-card">
        <div class="card-header-green"></div>
        <div class="pa-8 text-center">
          <v-icon size="48" color="grey lighten-1" class="mb-3">mdi-chart-box-outline</v-icon>
          <p class="mb-0 grey--text">No data for the selected filters. Try adjusting the date range or members.</p>
        </div>
      </div>

      <!-- Data Panels -->
      <div v-else class="panel-grid">
        <!-- Agent Performance -->
        <div class="modern-card customBorderRadius">
          <div class="card-header-green"></div>
          <div class="pa-4">
            <!-- Section header + inline search -->
            <div class="section-heading-row mb-3">
              <div>
                <div class="d-flex align-center">
                  <v-icon left color="primary" small>mdi-account-tie</v-icon>
                  <h2>Agent Performance</h2>
                </div>
                <p class="mb-0">Call count and average time spent per agent</p>
              </div>
              <v-text-field
                v-model="agentSearch"
                placeholder="Search agents..."
                prepend-inner-icon="mdi-magnify"
                dense
                outlined
                hide-details
                class="agent-search-inline"
                clearable
              />
            </div>

            <v-data-table
              :headers="agentHeaders"
              :items="agents"
              :items-per-page="15"
              :search="agentSearch"
              class="report-table elevation-0"
              dense
            >
              <template v-slot:item.name="{ item }">
                <div class="agent-cell">
                  <div class="agent-avatar">{{ item.name.charAt(0).toUpperCase() }}</div>
                  <div>
                    <div class="agent-name">{{ item.name }}</div>
                    <div class="agent-team">{{ item.teamName }}</div>
                  </div>
                </div>
              </template>
              <template v-slot:item.teamName="{ item }">
                <span style="display:none">{{ item.teamName }}</span>
              </template>
              <template v-slot:item.count="{ item }">
                <strong>{{ formatNumber(item.count) }}</strong>
              </template>
              <template v-slot:item.avgTimeSpent="{ item }">
                {{ formatMinutes(item.avgTimeSpent) }}
              </template>
              <template v-slot:item.totalTimeSpent="{ item }">
                {{ formatDuration(item.totalTimeSpent) }}
              </template>
              <template v-slot:item.targetAchievement="{ item }">
                <div class="target-achievement-cell">
                  <div class="target-achieved-text text-truncate" :title="formatNumber(item.count) + ' / ' + formatNumber(item.targetCallsTotal)">
                    {{ formatNumber(item.count) }} / {{ formatNumber(item.targetCallsTotal) }}
                  </div>
                </div>
              </template>
              <template v-slot:item.connectionRate="{ item }">
                <div class="connection-cell">
                  <v-progress-linear
                    :value="item.connectionRate"
                    :color="item.connectionRate >= 50 ? 'success' : 'warning'"
                    height="4"
                    rounded
                    class="connection-bar"
                  />
                  <span class="connection-pct" :class="item.connectionRate >= 50 ? 'success--text' : 'warning--text'">
                    {{ item.connectionRate }}%
                  </span>
                </div>
              </template>
            </v-data-table>

            <!-- Benchmark stats -->
            <div v-if="agents.length" class="benchmark-pills mt-3">
              <div class="benchmark-group">
                <span class="benchmark-group-label">Calls</span>
                <div class="benchmark-group-items">
                  <div class="benchmark-pill">
                    <span class="benchmark-pill-key">MIN</span>
                    <span class="benchmark-pill-val">{{ formatNumber(benchmarks.minCalls) }}</span>
                  </div>
                  <div class="benchmark-pill benchmark-pill--mid">
                    <span class="benchmark-pill-key">AVG</span>
                    <span class="benchmark-pill-val">{{ formatNumber(benchmarks.avgCalls) }}</span>
                  </div>
                  <div class="benchmark-pill">
                    <span class="benchmark-pill-key">MAX</span>
                    <span class="benchmark-pill-val">{{ formatNumber(benchmarks.maxCalls) }}</span>
                  </div>
                </div>
              </div>
              <v-divider vertical class="mx-3" />
              <div class="benchmark-group">
                <span class="benchmark-group-label">Avg Time</span>
                <div class="benchmark-group-items">
                  <div class="benchmark-pill">
                    <span class="benchmark-pill-key">MIN</span>
                    <span class="benchmark-pill-val">{{ formatMinutes(benchmarks.minAvgTimeSpent) }}</span>
                  </div>
                  <div class="benchmark-pill benchmark-pill--mid">
                    <span class="benchmark-pill-key">AVG</span>
                    <span class="benchmark-pill-val">{{ formatMinutes(benchmarks.avgAvgTimeSpent) }}</span>
                  </div>
                  <div class="benchmark-pill">
                    <span class="benchmark-pill-key">MAX</span>
                    <span class="benchmark-pill-val">{{ formatMinutes(benchmarks.maxAvgTimeSpent) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Disposition Breakdown -->
        <div class="modern-card customBorderRadius">
          <div class="card-header-green"></div>
          <div class="pa-4">
            <!-- Heading row + inline search -->
            <div class="section-heading-row mb-3">
              <div>
                <div class="d-flex align-center">
                  <v-icon left color="primary" small>mdi-tag-multiple</v-icon>
                  <h2>Disposition Breakdown</h2>
                </div>
                <p class="mb-0">Count and time spent by disposition</p>
              </div>
              <v-text-field
                v-model="dispositionSearch"
                placeholder="Search dispositions..."
                prepend-inner-icon="mdi-magnify"
                dense
                outlined
                hide-details
                class="agent-search-inline"
                clearable
              />
            </div>

            <!-- Top disposition bars -->
            <div v-if="topDispositions.length" class="dispo-bars-section mb-4">
              <v-expansion-panels flat accordion class="dispo-expansion-panels">
                <v-expansion-panel
                  v-for="item in topDispositions"
                  :key="item.dispo"
                  class="dispo-panel"
                  :disabled="!item.children || item.children.length === 0"
                >
                  <v-expansion-panel-header class="px-0 py-2">
                    <template v-slot:actions>
                      <v-icon v-if="item.children && item.children.length > 0" color="grey">mdi-chevron-down</v-icon>
                      <span v-else style="width: 24px; display: inline-block;"></span>
                    </template>
                    <div class="dispo-bar-row flex-grow-1 pr-3" style="width: 100%">
                      <div class="dispo-bar-label-row">
                        <div class="dispo-bar-dot" :style="{ background: item.hex }"></div>
                        <span class="dispo-bar-name">{{ item.dispo }}</span>
                        <span class="dispo-bar-count">
                          {{ formatNumber(item.count) }} ({{ item.sharePercent }}%)
                        </span>
                      </div>
                      <div class="dispo-bar-track">
                        <div
                          class="dispo-bar-fill"
                          :style="{ width: item.barWidthPercent + '%', background: item.hex }"
                        />
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="px-0 pb-0" v-if="item.children && item.children.length > 0">
                    <div
                      v-for="child in item.children"
                      :key="child.dispo"
                      class="dispo-bar-row mb-3 ml-6"
                    >
                      <div class="dispo-bar-label-row">
                        <div class="dispo-bar-dot" :style="{ background: child.hex || '#bdbdbd' }"></div>
                        <span class="dispo-bar-name text-body-2 grey--text text--darken-2">{{ child.dispo }}</span>
                        <span class="dispo-bar-count text-caption grey--text">
                          {{ formatNumber(child.count) }} ({{ child.sharePercent }}%)
                        </span>
                      </div>
                      <div class="dispo-bar-track" style="height: 6px;">
                        <div
                          class="dispo-bar-fill"
                          :style="{ width: child.barWidthPercent + '%', background: child.hex || '#bdbdbd' }"
                        />
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <!-- Table -->
            <v-data-table
              :headers="dispositionHeaders"
              :items="dispositions"
              :items-per-page="15"
              :search="dispositionSearch"
              class="report-table elevation-0"
              dense
              show-expand
              item-key="dispo"
            >
              <!-- Conditionally show expand icon -->
              <template v-slot:item.data-table-expand="{ item, expand, isExpanded }">
                <v-btn
                  v-if="item.children && item.children.length > 0"
                  icon
                  small
                  @click="expand(!isExpanded)"
                >
                  <v-icon :class="{ 'mdi-rotate-180': isExpanded }">mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              
              <template v-slot:item.dispo="{ item }">
                <div class="dispo-name-cell">
                  <span class="dispo-dot" :style="{ background: item.hex }"></span>
                  <span class="dispo-name-text">{{ item.dispo }}</span>
                </div>
              </template>
              <template v-slot:item.count="{ item }">
                <strong>{{ formatNumber(item.count) }}</strong>
              </template>
              <template v-slot:item.sharePct="{ item }">
                {{ formatPercent(item.sharePct) }}
              </template>
              <template v-slot:item.totalTimeSpent="{ item }">
                {{ formatDuration(item.totalTimeSpent) }}
              </template>
              <template v-slot:item.avgTimeSpent="{ item }">
                {{ formatSeconds(item.avgTimeSpent) }}
              </template>
              <template v-slot:item.avgTimeSpentMins="{ item }">
                {{ formatMinutes(item.avgTimeSpent) }}
              </template>
              
              <!-- Expanded slot for children -->
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0">
                  <v-data-table
                    :headers="headers.filter(h => h.value !== 'data-table-expand')"
                    :items="item.children"
                    hide-default-footer
                    hide-default-header
                    class="elevation-0 nested-table"
                    dense
                  >
                    <template v-slot:item.dispo="{ item: childItem }">
                      <div class="dispo-name-cell pl-6">
                        <span class="dispo-dot" :style="{ background: childItem.hex || '#bdbdbd' }"></span>
                        <span class="dispo-name-text text-body-2 grey--text text--darken-2">{{ childItem.dispo }}</span>
                      </div>
                    </template>
                    <template v-slot:item.count="{ item: childItem }">
                      {{ formatNumber(childItem.count) }}
                    </template>
                    <template v-slot:item.sharePct="{ item: childItem }">
                      {{ formatPercent(childItem.sharePct) }}
                    </template>
                    <template v-slot:item.totalTimeSpent="{ item: childItem }">
                      {{ formatDuration(childItem.totalTimeSpent) }}
                    </template>
                    <template v-slot:item.avgTimeSpent="{ item: childItem }">
                      {{ formatSeconds(childItem.avgTimeSpent) }}
                    </template>
                    <template v-slot:item.avgTimeSpentMins="{ item: childItem }">
                      {{ formatMinutes(childItem.avgTimeSpent) }}
                    </template>
                  </v-data-table>
                </td>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>

      <!-- Outcome Settings Dialog -->
      <v-dialog v-model="outcomeSettingsDialog" max-width="520">
        <v-card>
          <v-card-title class="headline">Successful / Unsuccessful Settings</v-card-title>
          <v-card-text>
            <p class="caption grey--text mb-4">
              Choose which dispositions count as successful or unsuccessful. Settings are saved in this browser only.
            </p>
            <v-autocomplete
              v-model="outcomeSettingsDraft.successfulDispos"
              :items="dispositionOptions"
              item-text="title"
              item-value="title"
              label="Successful dispositions"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              class="mb-4"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 2"
                  small
                  close
                  @click:close="outcomeSettingsDraft.successfulDispos = outcomeSettingsDraft.successfulDispos.filter(x => x !== (item.title || item))"
                >
                  {{ item.title || item }}
                </v-chip>
                <span
                  v-if="index === 2"
                  class="grey--text text-caption ml-2"
                >
                  (+{{ outcomeSettingsDraft.successfulDispos.length - 2 }} others)
                </span>
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="outcomeSettingsDraft.unsuccessfulDispos"
              :items="dispositionOptions"
              item-text="title"
              item-value="title"
              label="Unsuccessful dispositions"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 2"
                  small
                  close
                  @click:close="outcomeSettingsDraft.unsuccessfulDispos = outcomeSettingsDraft.unsuccessfulDispos.filter(x => x !== (item.title || item))"
                >
                  {{ item.title || item }}
                </v-chip>
                <span
                  v-if="index === 2"
                  class="grey--text text-caption ml-2"
                >
                  (+{{ outcomeSettingsDraft.unsuccessfulDispos.length - 2 }} others)
                </span>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="outcomeSettingsDialog = false">Cancel</v-btn>
            <v-btn color="primary" depressed @click="saveOutcomeSettings">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";

export default {
  name: "DispositionReport",
  data() {
    return {
      showHeader: true,
      loading: false,
      modal: false,
      dates: [],
      selectedUser: [],
      allUsers: [],
      dispos: [],
      excludedDispositions: [],
      agentSearch: "",
      dispositionSearch: "",
      outcomeSettingsDialog: false,
      outcomeSettings: {
        successfulDispos: [],
        unsuccessfulDispos: [],
      },
      outcomeSettingsDraft: {
        successfulDispos: [],
        unsuccessfulDispos: [],
      },
      outcomes: {
        successfulCalls: 0,
        unsuccessfulCalls: 0,
        successfulRate: 0,
        unsuccessfulRate: 0,
      },
      summary: {
        totalCalls: 0,
        totalTimeSpent: 0,
        avgTimeSpent: 0,
        connectedCalls: 0,
        connectionRate: 0,
      },
      benchmarks: {
        minCalls: 0,
        maxCalls: 0,
        avgCalls: 0,
        minAvgTimeSpent: 0,
        maxAvgTimeSpent: 0,
        avgAvgTimeSpent: 0,
      },
      agents: [],
      dispositions: [],
      agentHeaders: [
        { text: "Agent", value: "name", sortable: true, width: "200px" },
        { text: "Team", value: "teamName", sortable: false, align: " d-none" },
        { text: "Calls", value: "count", sortable: true, align: "end" },
        {
          text: "Avg Time (mins)",
          value: "avgTimeSpent",
          sortable: true,
          align: "end",
        },
        {
          text: "Total Time",
          value: "totalTimeSpent",
          sortable: true,
          align: "end",
        },
        {
          text: "Achieved / Target",
          value: "targetAchievement",
          sortable: true,
          width: "160px",
        },
        {
          text: "Connection",
          value: "connectionRate",
          sortable: true,
          width: "140px",
        },
      ],
      dispositionHeaders: [
        { text: "Disposition", value: "dispo", sortable: true },
        { text: "Count", value: "count", sortable: true, align: "end" },
        { text: "%", value: "sharePct", sortable: true, align: "end" },
        {
          text: "Total Time Spent",
          value: "totalTimeSpent",
          sortable: true,
          align: "end",
        },
        {
          text: "Avg Time Spent",
          value: "avgTimeSpent",
          sortable: true,
          align: "end",
        },
        {
          text: "Avg (mins)",
          value: "avgTimeSpentMins",
          sortable: false,
          align: "end",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "user", "teams", "selectedTeam"]),
    hasData() {
      return this.agents.length > 0 || this.dispositions.length > 0;
    },
    selectedTeamLabel() {
      if (this.selectedTeam === "allTeams") {
        return "All Teams";
      }
      const team = _.find(this.teams, { id: this.selectedTeam });
      return team ? team.name : "Team";
    },
    dateRangeLabel() {
      if (!this.dates || this.dates.length === 0) {
        return "";
      }
      const a = moment(this.dates[0]);
      const b = this.dates[1] ? moment(this.dates[1]) : a;
      const lo = a.isBefore(b) ? a : b;
      const hi = a.isBefore(b) ? b : a;
      if (lo.isSame(hi, "day")) {
        return lo.format("DD MMM YYYY");
      }
      return `${lo.format("DD MMM YYYY")} – ${hi.format("DD MMM YYYY")}`;
    },
    dispositionOptions() {
      return _.sortBy(
        _.filter(this.dispos, (d) => d.title && d.title !== "All Dispos"),
        "title",
      );
    },
    topDispositions() {
      const totalCalls = _.sumBy(this.dispositions, "count");
      const maxCount = _.maxBy(this.dispositions, "count");
      const peak = maxCount ? maxCount.count : 0;
      
      const processNode = (node) => {
        const count = Number(node.count || 0);
        const sharePercent =
          node.percent != null && node.percent !== ""
            ? Number(node.percent)
            : totalCalls > 0
              ? Number(((count / totalCalls) * 100).toFixed(1))
              : 0;

        return {
          ...node,
          sharePercent,
          barWidthPercent: peak > 0 ? Math.round((count / peak) * 100) : 0,
          children: (node.children || []).map(processNode)
        };
      };

      return _.take(this.dispositions, 8).map(processNode);
    },
    outcomeSettingsStorageKey() {
      return `dispositionReportOutcomeSettings_${this.user.companyId}`;
    },
  },
  watch: {
    async selectedTeam() {
      await this.fetchAccounts();
      await this.loadReport();
    },
  },
  async created() {
    this.showHeader =
      localStorage.getItem("dispositionReportHeaderDismissed") !== "true";
    this.loadOutcomeSettings();
    this.dates = [
      moment().subtract(6, "days").format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ];
    await Promise.all([this.fetchAccounts(), this.fetchDispos()]);
    await this.loadReport();
  },
  methods: {
    loadOutcomeSettings() {
      try {
        const raw = localStorage.getItem(this.outcomeSettingsStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          this.outcomeSettings = {
            successfulDispos: parsed.successfulDispos || [],
            unsuccessfulDispos: parsed.unsuccessfulDispos || [],
          };
        }
      } catch (e) {
        console.error("Failed to load outcome settings", e);
      }
    },
    openOutcomeSettings() {
      this.outcomeSettingsDraft = {
        successfulDispos: [...this.outcomeSettings.successfulDispos],
        unsuccessfulDispos: [...this.outcomeSettings.unsuccessfulDispos],
      };
      this.outcomeSettingsDialog = true;
    },
    saveOutcomeSettings() {
      this.outcomeSettings = {
        successfulDispos: [...this.outcomeSettingsDraft.successfulDispos],
        unsuccessfulDispos: [...this.outcomeSettingsDraft.unsuccessfulDispos],
      };
      localStorage.setItem(
        this.outcomeSettingsStorageKey,
        JSON.stringify(this.outcomeSettings),
      );
      this.outcomeSettingsDialog = false;
      this.loadReport();
    },
    enrichReportData(data, daySpan) {
      const safeDaySpan = Number(daySpan) > 0 ? Number(daySpan) : 1;
      const agents = data.agents || [];
      const dispositions = data.dispositions || [];
      const totalDispoCalls = _.sumBy(dispositions, (row) => row.count || 0);

      this.agents = agents.map((agent) => {
        const targetCallsPerDay =
          agent.targetCallsPerDay != null && Number(agent.targetCallsPerDay) > 0
            ? Number(agent.targetCallsPerDay)
            : 100;
        const targetCallsTotal =
          agent.targetCallsTotal != null && Number(agent.targetCallsTotal) > 0
            ? Number(agent.targetCallsTotal)
            : targetCallsPerDay * safeDaySpan;
        const count = Number(agent.count || 0);
        const targetAchievementRate =
          agent.targetAchievementRate != null
            ? Number(agent.targetAchievementRate)
            : targetCallsTotal > 0
              ? Number(((count / targetCallsTotal) * 100).toFixed(1))
              : 0;

        return {
          ...agent,
          targetCallsPerDay,
          targetCallsTotal,
          targetAchievementRate,
        };
      });

      // Build Disposition Hierarchy
      const treeMap = {};
      const tree = [];

      let mappedDispositions = dispositions.map((item) => {
        const dispoObj = this.dispos.find(d => d.title === item.dispo);
        return {
          ...item,
          isChild: dispoObj ? !!dispoObj.isChild : false,
          parentId: dispoObj ? dispoObj.parentId : null,
          dispoId: dispoObj ? dispoObj.id : null,
          hex: dispoObj ? dispoObj.hex : item.hex,
          children: []
        };
      });

      const getOrCreateParent = (parentId) => {
        if (treeMap[parentId]) return treeMap[parentId];
        
        const parentDispo = this.dispos.find(d => d.id === parentId);
        if (!parentDispo) return null;

        const newParent = {
          dispo: parentDispo.title,
          dispoId: parentId,
          count: 0,
          totalTimeSpent: 0,
          totalDuration: 0,
          connectedCalls: 0,
          hex: parentDispo.hex,
          isChild: false,
          parentId: null,
          children: []
        };
        treeMap[parentId] = newParent;
        tree.push(newParent);
        return newParent;
      };

      mappedDispositions.forEach(item => {
        if (item.isChild && item.parentId) {
          const parent = getOrCreateParent(item.parentId);
          if (parent) {
             if (parent.children.length === 0 && parent.count > 0) {
                 parent.children.push({
                     ...parent,
                     dispo: parent.dispo + ' (Direct)',
                     isDirect: true,
                     children: []
                 });
             }
             parent.children.push(item);
             parent.count += (item.count || 0);
             parent.totalTimeSpent += (item.totalTimeSpent || 0);
             parent.totalDuration += (item.totalDuration || 0);
             parent.connectedCalls += (item.connectedCalls || 0);
          } else {
             tree.push(item);
          }
        } else {
          if (item.dispoId && treeMap[item.dispoId]) {
             const existing = treeMap[item.dispoId];
             
             if (existing.children.length > 0 && (item.count || 0) > 0) {
                 existing.children.push({
                     ...item,
                     dispo: item.dispo + ' (Direct)',
                     isDirect: true,
                     children: []
                 });
             }
             
             existing.count += (item.count || 0);
             existing.totalTimeSpent += (item.totalTimeSpent || 0);
             existing.totalDuration += (item.totalDuration || 0);
             existing.connectedCalls += (item.connectedCalls || 0);
          } else {
             if (item.dispoId) {
                treeMap[item.dispoId] = item;
             }
             tree.push(item);
          }
        }
      });

      const totalCountForAll = _.sumBy(tree, 'count');
      
      tree.forEach(node => {
        const count = Number(node.count || 0);
        node.percent = totalCountForAll > 0 ? Number(((count / totalCountForAll) * 100).toFixed(1)) : 0;
        node.sharePct = node.percent;
        node.avgTimeSpent = count > 0 ? Math.floor(node.totalTimeSpent / count) : 0;
        
        if (node.children.length > 0) {
            node.children.forEach(child => {
                const childCount = Number(child.count || 0);
                child.percent = totalCountForAll > 0 ? Number(((childCount / totalCountForAll) * 100).toFixed(1)) : 0;
                child.sharePct = child.percent;
                child.avgTimeSpent = childCount > 0 ? Math.floor(child.totalTimeSpent / childCount) : 0;
            });
            node.children.sort((a, b) => (b.count || 0) - (a.count || 0));
        }
      });

      tree.sort((a, b) => (b.count || 0) - (a.count || 0));
      this.dispositions = tree;
    },
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("dispositionReportHeaderDismissed", "true");
    },
    getTeamIds() {
      if (this.selectedTeam === "allTeams") {
        return _.without(_.map(this.teams, "id"), "allTeams");
      }
      return [this.selectedTeam];
    },
    getAccountIds() {
      if (_.includes(this.selectedUser, "allMembers")) {
        return [];
      }
      return _.without(this.selectedUser, "allMembers");
    },
    async fetchDispos() {
      try {
        const res = await this.$http.get(
          `${this.$store.state.ENDPOINT}/dispos?filter[where][companyId]=${this.user.companyId}`,
        );
        this.dispos = res.body || [];
      } catch (e) {
        console.error("Failed to load dispositions", e);
      }
    },
    async fetchAccounts() {
      this.allUsers = [];
      try {
        const teams = this.getTeamIds();
        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/accounts/fetch-v2`,
          { teams },
        );
        this.allUsers = response.body || [];
        if (
          this.user.role === "Manager" ||
          this.user.role === "Teammanager" ||
          this.user.role === "Admin"
        ) {
          this.allUsers.unshift({
            id: "allMembers",
            name: "All Members",
          });
        }
        if (!this.selectedUser.length && this.allUsers.length) {
          this.selectedUser = [this.allUsers[0].id];
        }
      } catch (e) {
        const message =
          e.body && e.body.error && e.body.error.message
            ? e.body.error.message
            : "Error while fetching accounts";
        this.$swal({ type: "error", text: message });
      }
    },
    getOrderedDateRange() {
      const a = moment(this.dates[0]);
      const b = this.dates[1] ? moment(this.dates[1]) : a;
      const lo = a.isBefore(b) ? a : b;
      const hi = a.isBefore(b) ? b : a;
      return [lo.format("YYYY-MM-DD"), hi.format("YYYY-MM-DD")];
    },
    async loadReport() {
      if (!this.dates || !this.dates.length) {
        return this.$swal({
          type: "warning",
          text: "Please select a date range",
        });
      }

      const range = this.getOrderedDateRange();
      const daySpan = moment(range[1]).diff(moment(range[0]), "days") + 1;
      if (daySpan > 366) {
        return this.$swal({
          type: "warning",
          text: "Date range cannot exceed 366 days",
        });
      }

      this.loading = true;
      try {
        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/dispoStats/disposition-report`,
          {
            dateRange: range,
            teamsId: this.getTeamIds(),
            accountsId: this.getAccountIds(),
            excludeDispos: this.excludedDispositions,
            successfulDispos: this.outcomeSettings.successfulDispos,
            unsuccessfulDispos: this.outcomeSettings.unsuccessfulDispos,
          },
        );

        const data = response.body || {};
        this.summary = data.summary || this.summary;
        this.benchmarks = data.benchmarks || this.benchmarks;
        this.outcomes = data.outcomes || this.outcomes;
        this.enrichReportData(data, daySpan);
      } catch (e) {
        const message =
          e.body && e.body.error && e.body.error.message
            ? e.body.error.message
            : "Failed to load disposition report";
        this.$swal({ type: "error", text: message });
      } finally {
        this.loading = false;
      }
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },
    formatPercent(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) {
        return "0%";
      }
      return `${num}%`;
    },
    formatSeconds(ms) {
      const seconds = Number(ms || 0) / 1000;
      return `${seconds.toFixed(2)}s`;
    },
    formatMinutes(ms) {
      const minutes = Number(ms || 0) / 1000 / 60;
      return `${minutes.toFixed(2)} min`;
    },
    formatDuration(ms) {
      const totalSeconds = Number(ms || 0) / 1000;
      if (totalSeconds < 60) {
        return `${totalSeconds.toFixed(1)}s`;
      }
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = Math.round(totalSeconds % 60);
      if (minutes < 60) {
        return `${minutes}m ${seconds}s`;
      }
      const hours = Math.floor(minutes / 60);
      const remMinutes = minutes % 60;
      return `${hours}h ${remMinutes}m`;
    },
    exportReport() {
      if (!this.hasData) {
        return;
      }

      const agentRows = this.agents.map((agent) => ({
        Agent: agent.name,
        Team: agent.teamName,
        Calls: agent.count,
        "Avg Time Spent (mins)": (agent.avgTimeSpent / 1000 / 60).toFixed(2),
        "Total Time Spent (sec)": (agent.totalTimeSpent / 1000).toFixed(2),
        "Target Calls": agent.targetCallsTotal,
        "Achievement %": agent.targetAchievementRate,
        "Connection %": agent.connectionRate,
      }));

      const dispositionRows = this.dispositions.map((item) => ({
        Disposition: item.dispo,
        Count: item.count,
        "%": item.percent,
        "Total Time Spent (sec)": (item.totalTimeSpent / 1000).toFixed(2),
        "Avg Time Spent (sec)": (item.avgTimeSpent / 1000).toFixed(2),
        "Avg (mins)": (item.avgTimeSpent / 1000 / 60).toFixed(2),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(agentRows),
        "Agents",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(dispositionRows),
        "Dispositions",
      );

      const range = this.getOrderedDateRange();
      const filename = `Disposition_Report_${range[0]}_${range[1]}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },
  },
};
</script>

<style scoped>
/* ===== Container ===== */
.disposition-report-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100%;
}

.theme--dark .disposition-report-container {
  background-color: #121212 !important;
}

.disposition-report-shell {
  margin: 0 auto;
  max-width: 1440px;
  padding: 16px;
}

/* ===== Loading bar ===== */
.loading-bar {
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 20;
}

/* ===== Green header ===== */
.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.3);
  color: white;
  padding: 28px 32px;
}

.header-close-btn {
  position: absolute !important;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.header-icon-wrapper {
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  display: flex;
  height: 64px;
  justify-content: center;
  width: 64px;
}

.page-title {
  color: #ffffff;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0;
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin: 4px 0 0;
}

.feature-chip {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  border-color: rgba(255, 255, 255, 0.4) !important;
  color: white !important;
  font-weight: 500;
}

.feature-pills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
}

.feature-pill {
  align-items: center;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  font-size: 13px;
  font-weight: 500;
  justify-content: center;
  min-height: 32px;
  padding: 4px 14px;
  transition: all 0.2s ease;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

/* ===== Modern card ===== */
.modern-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  position: relative;
  transition: box-shadow 0.2s ease;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.customBorderRadius {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.card-header-green {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  height: 4px;
  width: 100%;
}

/* ===== Filter section ===== */
.filter-grid {
  display: grid;
  gap: 12px 16px;
  grid-template-columns: 1fr 1fr 1fr auto;
  align-items: end;
}

.filter-field-wrap {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.filter-field-label {
  align-items: center;
  color: #6d756f;
  display: flex;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.theme--dark .filter-field-label {
  color: #9e9e9e;
}

.filter-actions-wrap {
  justify-content: flex-end;
}

.filter-actions {
  display: flex;
  gap: 8px;
}

/* Override table-header text-transform leak into filter inputs */
.filter-input ::v-deep .v-select__selection,
.filter-input ::v-deep input::placeholder,
.filter-input ::v-deep .v-label {
  text-transform: none !important;
  letter-spacing: normal !important;
  font-size: 14px !important;
}

.filter-selection-text {
  font-size: 14px;
  text-transform: none !important;
  letter-spacing: normal !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-input ::v-deep .v-input__slot {
  min-height: 40px !important;
}

.filter-input ::v-deep .v-text-field__details {
  display: none;
}

.search-btn,
.export-btn {
  height: 40px !important;
}

.filter-summary {
  color: #6d756f;
  font-size: 13px;
}

.theme--dark .filter-summary {
  color: #9e9e9e;
}

/* ===== KPI Grid ===== */
.kpi-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.kpi-label {
  color: #6d756f;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  margin: 0 0 6px;
  text-transform: uppercase;
}

.theme--dark .kpi-label {
  color: #9e9e9e;
}

.kpi-value {
  color: #1f2a24;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.theme--dark .kpi-value {
  color: #f5f5f5;
}

.kpi-sub {
  color: #8a928c;
  font-size: 12px;
  margin: 6px 0 0;
}

.theme--dark .kpi-sub {
  color: #757575;
}

/* ===== Panel Grid ===== */
.panel-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

/* ===== Section Headings ===== */
.section-heading h2 {
  color: #1f2a24;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.theme--dark .section-heading h2 {
  color: #e0e0e0;
}

.section-heading p {
  color: #6d756f;
  font-size: 13px;
  margin: 2px 0 0;
}

.theme--dark .section-heading p {
  color: #9e9e9e;
}

/* Section heading row (heading left + search right) */
.section-heading-row {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.section-heading-row h2 {
  color: #1f2a24;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.theme--dark .section-heading-row h2 {
  color: #e0e0e0;
}

.section-heading-row p {
  color: #6d756f;
  font-size: 13px;
  margin: 2px 0 0;
}

.theme--dark .section-heading-row p {
  color: #9e9e9e;
}

.agent-search-inline {
  flex: 0 0 200px;
  max-width: 200px;
}

/* Agent cell */
.agent-cell {
  align-items: center;
  display: flex;
  gap: 10px;
  padding: 4px 0;
}

.agent-avatar {
  align-items: center;
  background: linear-gradient(135deg, #37cf37, #2ba82b);
  border-radius: 8px;
  color: #fff;
  display: flex;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  height: 30px;
  justify-content: center;
  width: 30px;
}

.agent-name {
  color: #1f2a24;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
}

.theme--dark .agent-name {
  color: #e0e0e0;
}

.agent-team {
  color: #9e9e9e;
  font-size: 11px;
  line-height: 1.2;
  margin-top: 1px;
}

/* Connection rate cell */
.connection-cell {
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 80px;
}

.connection-bar {
  flex: 1;
  min-width: 40px;
}

.connection-pct {
  font-size: 12px;
  font-weight: 600;
  min-width: 32px;
  text-align: right;
}

.target-achieved-text {
  color: #1f2a24;
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
}

.theme--dark .target-achieved-text {
  color: #e0e0e0;
}

.target-achievement-cell {
  min-width: 120px;
}

/* ===== Tables ===== */
.report-table {
  background: transparent !important;
}

.report-table ::v-deep thead tr {
  background: #f6faf6;
}

.theme--dark .report-table ::v-deep thead tr {
  background: #232323 !important;
}

.report-table ::v-deep thead th {
  color: #4a4a4a !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
}

.theme--dark .report-table ::v-deep thead th {
  color: #bdbdbd !important;
}

.report-table ::v-deep tbody tr:hover {
  background: rgba(55, 207, 55, 0.06) !important;
}

.theme--dark .report-table ::v-deep tbody tr:hover {
  background: rgba(55, 207, 55, 0.1) !important;
}

.table-search {
  max-width: 280px;
}

/* Hide the redundant standalone Team column (team shown in agent cell) */
.report-table ::v-deep th.d-none,
.report-table ::v-deep td.d-none {
  display: none !important;
}

/* ===== Benchmark pills ===== */
.benchmark-pills {
  align-items: stretch;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 0;
  padding-top: 12px;
  width: 100%;
}

.theme--dark .benchmark-pills {
  border-top-color: rgba(255, 255, 255, 0.1);
}

.benchmark-group {
  align-items: flex-start;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  padding: 0 12px;
}

.benchmark-group:first-child {
  padding-left: 0;
}

.benchmark-group-label {
  color: #9e9e9e;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  width: 100%;
}

.benchmark-group-items {
  display: flex;
  gap: 6px;
  width: 100%;
}

.benchmark-pill {
  align-items: center;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  padding: 8px 6px;
}

.theme--dark .benchmark-pill {
  background: rgba(255, 255, 255, 0.06);
}

.benchmark-pill--mid {
  background: rgba(55, 207, 55, 0.1);
  border: 1px solid rgba(55, 207, 55, 0.2);
}

.theme--dark .benchmark-pill--mid {
  background: rgba(55, 207, 55, 0.12);
}

.benchmark-pill-key {
  color: #9e9e9e;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.benchmark-pill-val {
  color: #1f2a24;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.theme--dark .benchmark-pill-val {
  color: #e0e0e0;
}

/* ===== Disposition bars (redesigned) ===== */
.dispo-bars-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dispo-bar-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dispo-bar-label-row {
  align-items: center;
  display: flex;
  gap: 8px;
}

.dispo-bar-dot {
  border-radius: 50%;
  flex-shrink: 0;
  height: 8px;
  width: 8px;
}

.dispo-bar-name {
  color: #1f2a24;
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme--dark .dispo-bar-name {
  color: #d0d0d0;
}

.dispo-bar-count {
  color: #6d756f;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.theme--dark .dispo-bar-count {
  color: #9e9e9e;
}

.dispo-bar-track {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
  width: 100%;
}

.theme--dark .dispo-bar-track {
  background: rgba(255, 255, 255, 0.08);
}

.dispo-bar-fill {
  border-radius: 999px;
  height: 100%;
  opacity: 0.85;
  transition: width 0.5s ease;
}

/* ===== Dispo table name cell ===== */
.dispo-name-cell {
  align-items: center;
  display: flex;
  gap: 8px;
}

.dispo-dot {
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  height: 8px;
  width: 8px;
}

.dispo-name-text {
  font-size: 13px;
  font-weight: 500;
}

/* ===== Loading card ===== */
.loading-card {
  color: #6d756f;
}

.theme--dark .loading-card {
  color: #9e9e9e;
}

/* ===== Responsive ===== */
@media (max-width: 1100px) {
  .panel-grid {
    grid-template-columns: 1fr;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 700px) {
  .disposition-report-shell {
    padding: 12px;
  }

  .kpi-grid {
    grid-template-columns: 1fr 1fr;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    width: 100%;
  }

  .search-btn,
  .export-btn {
    flex: 1;
  }

  .header-section {
    padding: 20px 20px;
  }

  .page-title {
    font-size: 22px;
  }

  .header-icon-wrapper {
    border-radius: 10px;
    height: 48px;
    width: 48px;
  }

  .feature-pills-container {
    margin-top: 14px;
  }

  .feature-pill {
    font-size: 12px;
    padding: 4px 10px;
  }

  .table-search {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .section-heading-row {
    flex-direction: column;
    gap: 10px;
  }

  .agent-search-inline {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .benchmark-pills {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .benchmark-group {
    padding: 0 !important;
  }
}
</style>
